import styled from 'styled-components'
import breakpoints from '../../../config/settings/breakpoints'

export const Container = styled.article`
  grid-column: 1 / -1;
  max-width: 760px;
  width: 100%;
  margin: 2rem auto 3rem;
  padding-inline: 1rem;

  @media ${breakpoints.device.sm} {
    grid-column: 2;
  }
`

export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 2rem;
  padding: 0;
  border: none;
  background: none;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`

export const HeroImage = styled.div`
  width: 100%;
  height: 220px;
  margin-bottom: 2.5rem;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.borderSubtle};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media ${breakpoints.device.sm} {
    height: 320px;
  }
`

export const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 2rem;
`

export const TechTag = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 2rem;
`

export const ExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-decoration: none;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    background: ${({ theme }) => theme.colors.borderSubtle};
  }
`

export const Section = styled.section`
  &:not(:last-child) {
    margin-bottom: 2.5rem;
  }
`

export const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.75rem;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const SectionBody = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const Summary = styled.p`
  margin: 0 0 1.5rem;
  font-size: 1.0625rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const BulletList = styled.ul`
  margin: 0;
  padding-inline-start: 1.25rem;
  list-style: disc;
  list-style-position: outside;

  li {
    display: list-item;
    font-size: 1rem;
    line-height: 1.7;
    color: ${({ theme }) => theme.colors.textSecondary};

    &:not(:last-child) {
      margin-bottom: 0.5rem;
    }
  }
`

export const DiagramFigure = styled.figure`
  margin: 0;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.borderSubtle};

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`

export const DiagramCaption = styled.figcaption`
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const GalleryGrid = styled.div`
  display: grid;
  gap: 1.25rem;
`

export const GalleryFigure = styled.figure`
  margin: 0;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.borderSubtle};

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`

export const GalleryCaption = styled.figcaption`
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const TimelineBlock = styled.div`
  padding: 1rem 1.25rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.borderSubtle};
`

export const TimelineTitle = styled.p`
  margin: 0 0 0.375rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const TimelineDescription = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const TechCategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`

export const TechCategory = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`

export const TechCategoryLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const TechGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

export const TechItemTag = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.borderSubtle};
`

export const MetricTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

export const MetricTag = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 0.4375rem 0.875rem;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.borderSubtle};
`

export const CodeLabel = styled.span`
  display: inline-block;
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const CodeBlock = styled.pre`
  margin: 0;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.borderSubtle};
  overflow-x: auto;

  code {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 0.875rem;
    line-height: 1.6;
    color: ${({ theme }) => theme.colors.textPrimary};
    white-space: pre;
  }
`

export const NotFound = styled.div`
  text-align: center;
  padding: 4rem 1rem;
  color: ${({ theme }) => theme.colors.textMuted};
`
